import React, { useRef, useState } from 'react';
import { X, Upload } from 'lucide-react';
import { Button } from './Button';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';
import toast from 'react-hot-toast';
import { MIN_HOURLY_RATE } from '../config/rateConstants';

const CERTIFICATIONS = [
  'MfN-register',
  'LVV-gecertificeerd',
  'NOBCO',
  'NIP',
  'SKJ',
  'BIG-geregistreerd',
  'NMv',
  'ADR',
];

interface Vacancy {
  id: string;
  titel: string;
  hourly_rate_max?: number;
  hours_per_week_min?: number;
  hours_per_week_max?: number;
}

interface Props {
  vacancy: Vacancy;
  onClose: () => void;
  onSuccess: () => void;
}

export const VacancyApplicationModal: React.FC<Props> = ({ vacancy, onClose, onSuccess }) => {
  const { user } = useAuthStore();
  const cvRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState({
    company_name: '',
    certifications: [] as string[],
    available_from: '',
    hours_per_week: '',
    rate_proposal: vacancy.hourly_rate_max ? String(vacancy.hourly_rate_max) : '',
    motivation: '',
    relevant_experience: '',
    confidentiality_declared: false,
  });
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingCv, setUploadingCv] = useState(false);

  const wordCount = form.motivation.trim() ? form.motivation.trim().split(/\s+/).length : 0;

  const toggleCert = (cert: string) => {
    setForm(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert],
    }));
  };

  const handleCvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setCvFile(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.confidentiality_declared) {
      toast.error('Bevestig de vertrouwelijkheidsverklaring');
      return;
    }
    if (!form.motivation.trim()) {
      toast.error('Voeg een motivatie toe');
      return;
    }

    setSubmitting(true);
    try {
      let cv_attachment_url: string | undefined;

      if (cvFile) {
        setUploadingCv(true);
        const fd = new FormData();
        fd.append('file', cvFile);
        const upRes = await fetch('/api/uploads', {
          method: 'POST',
          body: fd,
          credentials: 'include',
        });
        const upData = await upRes.json();
        setUploadingCv(false);
        if (!upRes.ok) {
          // Geweigerd bestand (type of medische-documenten-grendel): niet stil doorgaan zonder cv
          toast.error(upData.message || upData.error || 'Uploaden van het cv is mislukt');
          return;
        }
        if (upData.filename) {
          cv_attachment_url = upData.filename;
        }
      }

      await api.post(`/professional/vacancies/${vacancy.id}/reageer`, {
        motivation: form.motivation,
        company_name: form.company_name || undefined,
        certifications: form.certifications.length > 0 ? form.certifications : undefined,
        available_from: form.available_from || undefined,
        hours_per_week: form.hours_per_week ? parseInt(form.hours_per_week) : undefined,
        rate_proposal: form.rate_proposal ? parseFloat(form.rate_proposal) : undefined,
        relevant_experience: form.relevant_experience || undefined,
        confidentiality_declared: form.confidentiality_declared,
        cv_attachment_url,
      });

      toast.success('Sollicitatie ingediend!');
      onSuccess();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Fout bij indienen');
    } finally {
      setSubmitting(false);
      setUploadingCv(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 overflow-y-auto py-8 px-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Reageren op vacature</h2>
            <p className="text-sm text-gray-500 mt-0.5">{vacancy.titel}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="divide-y divide-gray-100">
          {/* A — Basisgegevens */}
          <section className="px-6 py-5 space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">A — Wie reageert er?</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Voornaam</label>
                <input
                  readOnly
                  value={user?.first_name || ''}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Achternaam</label>
                <input
                  readOnly
                  value={user?.last_name || ''}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">E-mailadres</label>
                <input
                  readOnly
                  value={user?.email || ''}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Bedrijfsnaam <span className="text-gray-400 font-normal">(optioneel)</span></label>
                <input
                  type="text"
                  value={form.company_name}
                  onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))}
                  placeholder="Naam van je praktijk of B.V."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </section>

          {/* B — Certificeringen */}
          <section className="px-6 py-5 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">B — Beroepsregistraties & Certificeringen</h3>
            <div className="grid grid-cols-2 gap-2">
              {CERTIFICATIONS.map(cert => (
                <label key={cert} className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.certifications.includes(cert)}
                    onChange={() => toggleCert(cert)}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">{cert}</span>
                </label>
              ))}
            </div>
          </section>

          {/* C — Beschikbaarheid & Tarief */}
          <section className="px-6 py-5 space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">C — Beschikbaarheid & Tarief</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Beschikbaar vanaf</label>
                <input
                  type="date"
                  value={form.available_from}
                  onChange={e => setForm(p => ({ ...p, available_from: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Uren per week</label>
                <input
                  type="number"
                  min={1}
                  max={40}
                  value={form.hours_per_week}
                  onChange={e => setForm(p => ({ ...p, hours_per_week: e.target.value }))}
                  placeholder="bv. 16"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Tariefvoorstel (€/uur)
                  {vacancy.hourly_rate_max && (
                    <span className="ml-1 text-gray-400 font-normal">max €{vacancy.hourly_rate_max}</span>
                  )}
                </label>
                <input
                  type="number"
                  min={MIN_HOURLY_RATE}
                  step={0.5}
                  value={form.rate_proposal}
                  onChange={e => setForm(p => ({ ...p, rate_proposal: e.target.value }))}
                  placeholder="bv. 95"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </section>

          {/* D — Motivatie & Ervaring */}
          <section className="px-6 py-5 space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">D — Motivatie & Ervaring</h3>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Waarom ben jij de juiste persoon voor deze opdracht?
                <span className={`ml-2 font-normal ${wordCount > 300 ? 'text-red-500' : 'text-gray-400'}`}>
                  {wordCount}/300 woorden
                </span>
              </label>
              <textarea
                required
                rows={5}
                value={form.motivation}
                onChange={e => setForm(p => ({ ...p, motivation: e.target.value }))}
                placeholder="Beschrijf kort je motivatie en relevante achtergrond..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Relevante ervaring met deze casuïstiek <span className="text-gray-400 font-normal">(optioneel)</span>
              </label>
              <textarea
                rows={3}
                value={form.relevant_experience}
                onChange={e => setForm(p => ({ ...p, relevant_experience: e.target.value }))}
                placeholder="bv. Ja, ik heb ervaring met arbeidsconflicten in de zorgsector..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>
          </section>

          {/* E — Bijlagen */}
          <section className="px-6 py-5 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">E — Bijlagen</h3>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">
                CV of portfolio <span className="text-gray-400 font-normal">(optioneel, max 10 MB)</span>
              </label>
              <input ref={cvRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleCvChange} />
              <button
                type="button"
                onClick={() => cvRef.current?.click()}
                className="flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
              >
                <Upload className="w-4 h-4" />
                {cvFile ? cvFile.name : 'Kies bestand (PDF, Word)'}
              </button>
              {uploadingCv && <p className="text-xs text-gray-500 mt-1">Bestand uploaden...</p>}
            </div>
          </section>

          {/* F — Verklaring */}
          <section className="px-6 py-5">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                required
                checked={form.confidentiality_declared}
                onChange={e => setForm(p => ({ ...p, confidentiality_declared: e.target.checked }))}
                className="mt-0.5 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">
                Ik verklaar dat ik vertrouwelijk omga met de gedeelde informatie over de opdrachtgever, medewerkers en casuïstiek.
              </span>
            </label>
          </section>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Annuleren
            </Button>
            <Button
              type="submit"
              disabled={submitting || wordCount > 300}
              loading={submitting}
            >
              {submitting ? 'Indienen...' : 'Sollicitatie indienen'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
